import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, FileSpreadsheet, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge, Empty, Field, SelectField } from "@/components/common";
import { api, downloadFile, formatApiError, openInterventionPdf } from "@/lib/api";
import { useList } from "@/lib/hooks";
import { BILLING_STATUS, fmtMin, fmtPrice, todayStr, fmtDate } from "@/lib/constants";

const SCOPES = { to_bill: "À facturer", facturee: "Facturées", offerte: "Offertes", non_facturable: "Non facturables", all: "Toutes les réalisées" };

const MarkDialog = ({ open, onClose, onConfirm }) => {
  const [f, setF] = useState({ number: "", date: todayStr() });
  useEffect(() => { if (open) setF({ number: "", date: todayStr() }); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md border-cyan-400/20 bg-[#0F1F3D]" data-testid="billing-mark-dialog">
        <DialogTitle>Facture établie</DialogTitle>
        <p className="text-sm text-slate-400">Enregistre le numéro et la date de la facture créée dans votre logiciel. Rien n'est envoyé au client.</p>
        <Field label="N° de facture *"><Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="billing-mark-number-input" /></Field>
        <Field label="Date de facture"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="billing-mark-date-input" /></Field>
        <Button className="dt-glow h-11" disabled={!f.number.trim()} onClick={() => onConfirm(f)} data-testid="billing-mark-confirm-button">Marquer comme facturée(s)</Button>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ r, checked, onCheck }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-white/5 bg-white/[0.03] p-3" data-testid={`billing-row-${r.id}`}>
    <Checkbox checked={checked} onCheckedChange={onCheck} className="h-5 w-5" data-testid={`billing-row-check-${r.id}`} />
    <div className="min-w-0 flex-1 basis-48">
      <Link to={`/interventions/${r.id}`} className="font-medium text-white hover:text-cyan-300" data-testid={`billing-row-open-${r.id}`}>{r.title}</Link>
      <p className="text-xs text-slate-400">N° {r.number} · {fmtDate(r.date)} · {r.client_name || "Sans client"}{r.invoice_number ? ` · Facture ${r.invoice_number} du ${fmtDate(r.invoice_date)}` : ""}</p>
    </div>
    <span className="font-mono-dt text-xs text-slate-300">{fmtMin(r.computed.billable_minutes)}</span>
    <Badge value={r.computed.billing_status} map={BILLING_STATUS} />
    <span className="w-24 text-right font-semibold text-white" data-testid={`billing-row-amount-${r.id}`}>{fmtPrice(r.computed.to_bill)}</span>
    <Button size="icon" variant="ghost" title="Bon d'intervention PDF" onClick={() => openInterventionPdf(r.id).catch((e) => toast.error(formatApiError(e)))} data-testid={`billing-row-pdf-${r.id}`}><FileText className="h-4 w-4" /></Button>
  </div>
);

export const BillingList = () => {
  const [flt, setFlt] = useState({ scope: "to_bill", client_id: null, q: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [mark, setMark] = useState(false);
  const { items: clients } = useList("/clients");
  const params = useMemo(() => Object.fromEntries(Object.entries(flt).filter(([, v]) => v)), [flt]);
  const load = useCallback(() => api.get("/billing/interventions", { params }).then((r) => setData(r.data)), [params]);
  useEffect(() => { load(); setSel([]); }, [load]);
  const rows = data?.rows || [];
  const toggle = (r, on) => {
    if (on && sel.length && rows.find((x) => x.id === sel[0])?.client_id !== r.client_id) {
      toast.error("Sélectionnez des interventions d'un même client pour une facturation groupée");
      return;
    }
    setSel((s) => (on ? [...s, r.id] : s.filter((x) => x !== r.id)));
  };
  const apply = async (status, inv = {}) => {
    try {
      await api.post("/billing/mark", { ids: sel, status, invoice_number: inv.number || "", invoice_date: inv.date || null });
      toast.success("Statut de facturation mis à jour");
      setMark(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const exp = (format) => downloadFile("/billing/export", { ...params, format, ...(sel.length ? { ids: sel.join(",") } : {}) }, format === "csv" ? `dtech42-facturation-${todayStr()}.csv` : undefined)
    .catch((e) => toast.error(formatApiError(e)));
  const selTotal = rows.filter((r) => sel.includes(r.id)).reduce((s, r) => s + r.computed.to_bill, 0);
  const clientOpts = (clients || []).map((c) => [c.id, c.name]);
  return (
    <div className="space-y-4" data-testid="billing-list">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <SelectField value={flt.scope} onChange={(v) => setFlt({ ...flt, scope: v })} options={SCOPES} testid="billing-scope-select" />
        <SelectField value={flt.client_id} onChange={(v) => setFlt({ ...flt, client_id: v })} options={clientOpts} allowNone="Tous les clients" testid="billing-client-select" />
        <Input placeholder="Rechercher…" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} className="col-span-2 md:col-span-1" data-testid="billing-search-input" />
        <Input type="date" value={flt.date_from} onChange={(e) => setFlt({ ...flt, date_from: e.target.value })} data-testid="billing-date-from-input" />
        <Input type="date" value={flt.date_to} onChange={(e) => setFlt({ ...flt, date_to: e.target.value })} data-testid="billing-date-to-input" />
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-cyan-400/20 bg-[#122549]/70 p-4">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-slate-400">{SCOPES[flt.scope]} · {rows.length} intervention(s)</p>
          <p className="font-display text-2xl font-semibold text-cyan-300" data-testid="billing-list-total">{fmtPrice(data?.total_to_bill)}</p>
          {data?.vat_mention && <p className="text-xs text-slate-500">{data.vat_mention}</p>}
        </div>
        <Button variant="outline" className="h-11" onClick={() => exp("csv")} data-testid="billing-export-csv-button"><FileSpreadsheet className="mr-2 h-4 w-4" />CSV{sel.length ? " (sélection)" : ""}</Button>
        <Button variant="outline" className="h-11" onClick={() => exp("pdf")} data-testid="billing-export-pdf-button"><FileText className="mr-2 h-4 w-4" />PDF{sel.length ? " (sélection)" : ""}</Button>
      </div>
      {sel.length > 0 && (
        <div className="sticky bottom-20 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-cyan-400/40 bg-[#0A1630]/95 p-3 lg:bottom-4" data-testid="billing-selection-bar">
          <span className="flex-1 text-sm text-white">{sel.length} sélectionnée(s) · {fmtPrice(selTotal)}</span>
          <Button className="dt-glow h-11" onClick={() => setMark(true)} data-testid="billing-selection-invoice-button"><Receipt className="mr-2 h-4 w-4" />Facturée</Button>
          <SelectField value={null} onChange={(v) => v && apply(v)} options={{ non_facturee: "Non facturée", offerte: "Offerte", non_facturable: "Non facturable" }} placeholder="Autre statut…" testid="billing-selection-status-select" className="h-11 w-40" />
        </div>
      )}
      {data && !rows.length && <Empty testid="billing-empty">Aucune intervention pour ces critères.</Empty>}
      <div className="space-y-2">{rows.map((r) => <Row key={r.id} r={r} checked={sel.includes(r.id)} onCheck={(on) => toggle(r, on)} />)}</div>
      <MarkDialog open={mark} onClose={() => setMark(false)} onConfirm={(inv) => apply("facturee", inv)} />
    </div>
  );
};
